export const idOf = (value) => String(value?._id || value?.id || value || "");
export const nameOf = (value) =>
  value?.fullName || value?.name || (typeof value === "string" ? value : "Not available");
export const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
let draftSequence = 0;
export const newAssignment = () => ({
  localId: `draft-${Date.now()}-${++draftSequence}`,
  tutorId: "",
  subjects: [],
  classDays: [],
  finalisedActiveSlot: "",
  duration: 60,
  allocatedClasses: 8,
  payBasis: "PER_CLASS",
  payRate: "",
});
export const minutes = (time) => {
  const match = String(time || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  if (Number(match[2]) > 59 || (match[3] ? hour < 1 || hour > 12 : hour > 23)) return null;
  if (match[3]) hour = (hour % 12) + (match[3].toUpperCase() === "PM" ? 12 : 0);
  return hour * 60 + Number(match[2]);
};
export const overlaps = (a, b) => {
  if (!a.classDays?.some((day) => b.classDays?.includes(day))) return false;
  const startA = minutes(a.slot?.startTime),
    endA = minutes(a.slot?.endTime);
  const startB = minutes(b.slot?.startTime),
    endB = minutes(b.slot?.endTime);
  if ([startA, endA, startB, endB].some((v) => v === null)) return false;
  return startA < endB && startB < endA;
};
export const estimatedCost = (row) => {
  if (row.payRate === "" || row.payRate == null) return null;
  return Number(row.allocatedClasses) * Number(row.payRate);
};
export const validateSME = (form) => {
  const errors = [];
  if (!form.parentId) errors.push("Select a parent.");
  if (form.students?.length !== 1) errors.push("Select exactly one student.");
  if (!Number.isInteger(Number(form.noOfMonths)) || Number(form.noOfMonths) < 1)
    errors.push("Enter a positive whole number of months.");
  if (
    form.paymentAmount === "" ||
    !Number.isFinite(Number(form.paymentAmount)) ||
    Number(form.paymentAmount) < 0
  )
    errors.push("Enter a valid package fee.");
  if (!form.tutorAssignments?.length) errors.push("Add at least one tutor.");
  (form.tutorAssignments || []).forEach((row, i, rows) => {
    const label = `Tutor ${i + 1}: `;
    if (!row.tutorId || !row.subjects.length || !row.classDays.length || !row.finalisedActiveSlot)
      errors.push(label + "select tutor, subjects, days and slot.");
    if (
      !Number.isFinite(Number(row.duration)) ||
      Number(row.duration) <= 0 ||
      !Number.isInteger(Number(row.allocatedClasses)) ||
      Number(row.allocatedClasses) <= 0
    )
      errors.push(label + "enter positive duration and whole allocated classes.");
    if (row.payRate === "" || !Number.isFinite(Number(row.payRate)) || Number(row.payRate) < 0)
      errors.push(label + "enter a valid per-session fee.");
    if (
      row.slot &&
      minutes(row.slot.endTime) - minutes(row.slot.startTime) !== Number(row.duration)
    )
      errors.push(label + "duration must match the selected slot.");
    if (row.availabilityError || row.availabilityLoading)
      errors.push(label + "wait for availability or retry loading it.");
    if (row.busySessions?.some((session) => overlaps(row, session)))
      errors.push(label + "selected schedule overlaps an existing tutor class.");
    rows.slice(0, i).forEach((other) => {
      if (
        row.tutorId &&
        row.tutorId === other.tutorId &&
        row.subjects.some((s) => other.subjects.includes(s))
      )
        errors.push(label + "duplicate tutor/subject assignment.");
      if (overlaps(row, other))
        errors.push(label + "schedule overlaps another assignment for this student.");
    });
  });
  return errors;
};
// Draft-only contract. No caller should send this until SME backend support exists.
export const buildSMEPayload = (form) => ({
  classType: "SME",
  parentId: form.parentId,
  students: [...form.students],
  mode: form.mode,
  noOfMonths: Number(form.noOfMonths ?? 1),
  studentBilling: { billingBasis: "PACKAGE", totalAmount: Number(form.paymentAmount) },
  totalNoOfClasses: form.tutorAssignments.reduce(
    (sum, row) => sum + Number(row.allocatedClasses || 0),
    0
  ),
  tutorAssignments: form.tutorAssignments.map((row) => ({
    tutorId: row.tutorId,
    subjects: [...row.subjects],
    classDays: [...row.classDays],
    finalisedActiveSlot: row.finalisedActiveSlot,
    duration: Number(row.duration),
    allocatedClasses: Number(row.allocatedClasses),
    payBasis: "PER_CLASS",
    payRate: Number(row.payRate),
  })),
});
